import Modal from '@mui/material/Modal';
import { FC, useState, useEffect } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import { Document, Page, pdfjs } from 'react-pdf';
import Paper from '@mui/material/Paper';
import { getUserCertificates, Certificate, createAttachedSignature } from 'crypto-pro';
import CertificateModal from './CertificateModal';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const postCertificatesRejectURL = process.env.REACT_APP_API_URL + 'api/Certificates/Reject';

const postCreateRejectPdfURL = process.env.REACT_APP_API_URL + 'api/Certificates/CreateRejectPdf';

const getRejectReasonsURL = process.env.REACT_APP_API_URL + 'api/Certificates/GetRejectReasons';

const DenialModal: FC<{
    handleClose: (isNeedRefetch: boolean) => void;
    id: number;
}> = ({ handleClose, id }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [pdfData, setPdfData] = useState<any>(undefined);
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
    const [isLoadingCertificates, setIsLoadingCertificates] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [reasons, setReasons] = useState([]);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [snackbarText, setSnackbarText] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

    const handleChange = (key: string) => {
        setIsLoading(true);
        setRejectionReason(key);

        try {
            axios
                .post(
                    postCreateRejectPdfURL,
                    {
                        statementId: id,
                        rejectionReason: key,
                        certId: '1',
                        certOwner: localStorage.getItem('user_fio'),
                    },
                    {
                        responseType: 'blob',
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')}`,
                        },
                    },
                )
                .then((data) => {
                    const blob = new Blob([data.data], { type: 'application/pdf' });

                    setPdfData(blob);
                })
                .catch((err) => {
                    console.log('err', err);
                    handleClose(true);
                })
                .finally(() => setIsLoading(false));
        } catch (err) {
            console.log('err', err);
        }
    };

    const someSigh = async () => {
        setIsLoadingCertificates(true);

        try {
            const certificates = await getUserCertificates();

            setCertificates(certificates);
            setIsLoadingCertificates(false);
            setIsCertificateModalOpen(true);
        } catch (error) {
            alert('Не удалось загрузить список сертификатов');
            setIsLoadingCertificates(false);
        }
    };

    const handleCertificateModalClose = () => setIsCertificateModalOpen(false);

    const handleSelectedCertificateForSign = async (сertificate: Certificate) => {
        try {
            pdfData.arrayBuffer().then((res: any) => {
                createAttachedSignature(сertificate.thumbprint, res).then((signature) =>
                    axios
                        .post(
                            postCertificatesRejectURL,
                            {
                                statementId: id,
                                rejectionReason: rejectionReason,
                                signature: signature,
                            },
                            {
                                headers: {
                                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                                },
                            },
                        )
                        .then((data) => {
                            setIsSnackbarOpen(true);
                            setSnackbarSeverity('success');
                            setSnackbarText('Документ успешно подписан в отказе');
                            handleClose(true);
                        })
                        .catch((err) => {
                            alert(`Не удалось подписать документ: ${err}`);
                        }),
                );
            });
        } catch (error) {
            alert(`Не удалось создать подпись: ${error}`);
        }
    };

    useEffect(() => {
        setIsLoading(true);

        try {
            axios
                .get(getRejectReasonsURL
                    , {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                }
            )
                .then((data) => {
                    setReasons(data.data);
                    setIsLoading(false);
                });
        } catch (err) {
            alert(`Не получилось загрузить причины отказа, ${err}`);
        }
    }, []);

    const handleCloseSnackbar = () => setIsSnackbarOpen(false);

    return (
        <Modal open={true} onClose={() => handleClose(false)}>
            <>
                {pdfData ? (
                    <Box display='flex' width='100%' height='100%' justifyContent='center' alignItems='center'>
                        <Paper
                            sx={{
                                width: 650,
                                height: 550,
                                padding: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '24px',
                            }}
                        >
                            <Box display='flex' width={650} height={500} sx={{ overflowY: 'scroll' }}>
                                <Document file={pdfData}>
                                    <Page pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} />
                                </Document>
                            </Box>
                            <Box display='flex' width={600} justifyContent='space-between'>
                                <Button variant='contained' onClick={() => handleClose(false)}>
                                    Отменить
                                </Button>
                                {
                                    <Button
                                        variant='contained'
                                        onClick={someSigh}
                                        disabled={isLoadingCertificates}
                                        sx={{ width: '121px' }}
                                    >
                                        {isLoadingCertificates ? (
                                            <CircularProgress size={20} sx={{ color: 'white' }} />
                                        ) : (
                                            'Подписать'
                                        )}
                                    </Button>
                                }
                            </Box>
                        </Paper>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            position: 'absolute' as 'absolute',
                            top: '20%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            border: '2px solid #000',
                            boxShadow: 24,
                            p: 4,
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress />
                        ) : (
                            <FormControl fullWidth>
                                <InputLabel>Выберите причину отказа</InputLabel>
                                <Select label='Выберите причину отказа'>
                                    {reasons.map(({ name, systemName }) => (
                                        <MenuItem
                                            key={systemName}
                                            value={systemName}
                                            onClick={() => handleChange(systemName)}
                                            sx={{
                                                textWrap: 'auto',
                                                borderBottom: '1px solid gray',

                                                ':last-child': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                )}
                <Snackbar
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    open={isSnackbarOpen}
                    onClose={handleCloseSnackbar}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbarSeverity}
                        variant='filled'
                        sx={{ width: '100%' }}
                    >
                        {snackbarText}
                    </Alert>
                </Snackbar>
                <CertificateModal
                    open={isCertificateModalOpen}
                    certificates={certificates}
                    handleClose={handleCertificateModalClose}
                    handleSelectedCertificateForSign={handleSelectedCertificateForSign}
                />
            </>
        </Modal>
    );
};

export default DenialModal;
